import TomSelect from "tom-select";
import "tom-select/dist/css/tom-select.css";
import Swal from "sweetalert2";
import { ApiService } from "../services/ApiService.js";


const LS_KEY = "app.location.selected";

/**
 * ============================
 *  PRIVATE HELPERS
 * ============================
 */

function _qs(root, selector, name) {
	const el = root.querySelector(selector);
	if (!el) throw new Error(`LocationSelectModule: no se encontró ${name} (${selector})`);
	if (el.tagName !== "SELECT") throw new Error(`LocationSelectModule: ${name} no es SELECT (${el.tagName})`);
	return el;
}

function _destroyIfExists(el) {
	if (el?.tomselect) {
		el.tomselect.destroy();
		el.tomselect = null;
	}
}

function _createTom(el, placeholder) {
	return new TomSelect(el, {
		create: false,
		placeholder,
		closeAfterSelect: true,
		maxOptions: 5000,
		searchField: ["text"],
		hidePlaceholder: true,


		render: {
			no_results: function () {
				return `<div class="no-results p-2 text-muted">Sin resultados</div>`;
			},
		},
	});
}

function _mapToOptions(arr) {
	return (arr || []).map((x) => ({
		value: String(x.id),
		text: x.name,
	}));
}

/**
 * ============================
 *  PRIVATE API
 * ============================
 */

async function _getPaises() {
	const response = await ApiService.post("paises/obtener/");
	if (response.status !== 0) return [];

	return response.message.map((item) => ({
		id: item.id,
		name: item.nombre,
	}));
}

async function _getProvinciasByPais(paisId) {
	const response = await ApiService.post(`provincias/ObtenerDePais?IdPais=${paisId}`);
	if (response.status !== 0) return [];

	return response.message.map((item) => ({
		id: item.id,
		name: item.nombre,
	}));
}

async function _getLocalidadesByProvincia(provinciaId) {
	const response = await ApiService.post(`localidades/ObtenerDeProvincia?IdProvincia=${provinciaId}`);
	if (response.status !== 0) return [];

	return response.message.map((item) => ({
		id: item.id,
		name: item.nombre,
	}));
}

/**
 * ============================
 *  PRIVATE LOADERS
 * ============================
 */

async function _loadPaises(tomPais) {
	const paises = await _getPaises();
	tomPais.clearOptions();
	tomPais.addOptions(_mapToOptions(paises));
	tomPais.refreshOptions(false);
}

async function _loadProvincias(tomProvincia, paisId) {
	const provincias = await _getProvinciasByPais(paisId);
	tomProvincia.clear(true);
	tomProvincia.clearOptions();
	tomProvincia.addOptions(_mapToOptions(provincias));
	tomProvincia.refreshOptions(false);
}

async function _loadLocalidades(tomLocalidad, provinciaId) {
	const localidades = await _getLocalidadesByProvincia(provinciaId);
	tomLocalidad.clear(true);
	tomLocalidad.clearOptions();
	tomLocalidad.addOptions(_mapToOptions(localidades));
	tomLocalidad.refreshOptions(false);
}

function _getSavedLocation() {
	try {
		const raw = localStorage.getItem(LS_KEY);
		if (!raw) return null;

		const obj = JSON.parse(raw);
		if (!obj) return null;

		const paisId = Number(obj.paisId);
		const provinciaId = Number(obj.provinciaId);
		const localidadId = obj.localidadId ? Number(obj.localidadId) : null;

		if (!Number.isFinite(paisId) || paisId <= 0) return null;
		if (!Number.isFinite(provinciaId) || provinciaId <= 0) return null;

		return { paisId, provinciaId, localidadId };
	} catch (e) {
		console.warn("LocationSelectModule: error leyendo localStorage", e);
		return null;
	}
}

function _saveLocation(loc) {
	if (!loc?.paisId || !loc?.provinciaId) return;
	localStorage.setItem(
		LS_KEY,
		JSON.stringify({
			paisId: Number(loc.paisId),
			provinciaId: Number(loc.provinciaId),
			localidadId: loc.localidadId ? Number(loc.localidadId) : null,
		})
	);
}

async function _clearSavedLocation() {
	localStorage.removeItem(LS_KEY);
}

/**
 * ============================
 *  PUBLIC MODULE
 * ============================
 */

export const LocationSelectModule = {
	/**
	 * Mount en pantalla (form)
	 * Soporta:
	 *  - País -> Provincia -> Localidad
	 *  - País -> Provincia (si selectLocalidad = null)
	 */
	mount: async ({
		root = document,

		selectPais,
		selectProvincia,
		selectLocalidad = null,

		placeholderPais = "País",
		placeholderProvincia = "Provincia",
		placeholderLocalidad = "Localidad",

		initial = { paisId: null, provinciaId: null, localidadId: null },

		loadSavedLocation = false,

		onChange = { pais: null, provincia: null, localidad: null },
	}) => {
		const $pais = _qs(root, selectPais, "selectPais");
		const $provincia = _qs(root, selectProvincia, "selectProvincia");
		const $localidad = selectLocalidad ? _qs(root, selectLocalidad, "selectLocalidad") : null;


		initial= loadSavedLocation ? _getSavedLocation() : initial,

		_destroyIfExists($pais);
		_destroyIfExists($provincia);
		if ($localidad) _destroyIfExists($localidad);

		const tomPais = _createTom($pais, placeholderPais);
		const tomProvincia = _createTom($provincia, placeholderProvincia);
		const tomLocalidad = $localidad ? _createTom($localidad, placeholderLocalidad) : null;

		// Estado inicial
		tomProvincia.disable();
		if (tomLocalidad) tomLocalidad.disable();

		// Cargar países
		await _loadPaises(tomPais);

		// Handlers
		const handlePaisChange = async (paisId) => {
			tomProvincia.clear(true);
			tomProvincia.clearOptions();

			if (tomLocalidad) {
				tomLocalidad.clear(true);
				tomLocalidad.clearOptions();
				tomLocalidad.disable();
			}

			if (!paisId) {
				tomProvincia.disable();
				if (onChange?.pais) onChange.pais(null);
				return;
			}

			tomProvincia.enable();
			await _loadProvincias(tomProvincia, paisId);

			if (onChange?.pais) onChange.pais(paisId);
		};

		const handleProvinciaChange = async (provinciaId) => {
			if (tomLocalidad) {
				tomLocalidad.clear(true);
				tomLocalidad.clearOptions();
			}

			if (!provinciaId) {
				if (tomLocalidad) tomLocalidad.disable();
				if (onChange?.provincia) onChange.provincia(null);
				return;
			}

			if (tomLocalidad) {
				tomLocalidad.enable();
				await _loadLocalidades(tomLocalidad, provinciaId);
			}

			if (onChange?.provincia) onChange.provincia(provinciaId);
		};

		const handleLocalidadChange = (localidadId) => {
			if (onChange?.localidad) onChange.localidad(localidadId || null);
		};

		// Eventos
		tomPais.on("change", handlePaisChange);
		tomProvincia.on("change", handleProvinciaChange);
		if (tomLocalidad) tomLocalidad.on("change", handleLocalidadChange);

		// Precarga
		if (initial?.paisId) {
			tomPais.setValue(String(initial.paisId), true);
			await handlePaisChange(String(initial.paisId));

			if (initial?.provinciaId) {
				tomProvincia.setValue(String(initial.provinciaId), true);
				await handleProvinciaChange(String(initial.provinciaId));

				if (tomLocalidad && initial?.localidadId) {
					tomLocalidad.setValue(String(initial.localidadId), true);
				}
			}
		}

		if (loadSavedLocation) _saveLocation({
			paisId: tomPais.getValue() || null,
			provinciaId: tomProvincia.getValue() || null,
			localidadId: tomLocalidad ? (tomLocalidad.getValue() || null) : null,
		});

		return {
			tomPais,
			tomProvincia,
			tomLocalidad,
			getValue: () => ({
				paisId: tomPais.getValue() || null,
				provinciaId: tomProvincia.getValue() || null,
				localidadId: tomLocalidad ? (tomLocalidad.getValue() || null) : null,
			}),
			destroy: () => {
				tomPais.destroy();
				tomProvincia.destroy();
				if (tomLocalidad) tomLocalidad.destroy();
			},
		};
	},

	/**
	 * Picker (Swal)
	 * Devuelve:
	 *  - null si canceló
	 *  - { paisId, provinciaId, localidadId }
	 */
	pick: async ({
		title = "Seleccioná tu ubicación",
		confirmText = "Continuar",
		cancelText = "Cancelar",
		showLocalidad = false,
		initial = { paisId: null, provinciaId: null, localidadId: null },
		loadSavedLocation = false,
	} = {}) => {
		let instance = null;

		const html = `
      <div class="text-start">
        <div class="mb-3">
          <label class="form-label mb-1">País</label>
          <select id="swalSelectPais" class="form-select" required>
            <option value="">Seleccionar país</option>
          </select>
        </div>

        <div class="${showLocalidad ? "mb-3" : "mb-1"}">
          <label class="form-label mb-1">Provincia</label>
          <select id="swalSelectProvincia" class="form-select" required>
            <option value="">Seleccionar provincia</option>
          </select>
        </div>

        ${showLocalidad
				? `
          <div class="mb-1">
            <label class="form-label mb-1">Localidad</label>
            <select id="swalSelectLocalidad" class="form-select" required>
              <option value="">Seleccionar localidad</option>
            </select>
          </div>
        `
				: ""
			}
      </div>
	  <div style="height:140px"></div>
    `;

		const result = await Swal.fire({
			title,
			html,
			confirmButtonText: confirmText,
			cancelButtonText: cancelText,
			showCancelButton: true,
			focusConfirm: false,
			allowOutsideClick: false,
			width: 650,
			padding: "1.5rem",

			didOpen: async () => {
				const popup = Swal.getPopup();
				if (!popup) return;

				instance = await LocationSelectModule.mount({
					root: popup,
					selectPais: "#swalSelectPais",
					selectProvincia: "#swalSelectProvincia",
					selectLocalidad: showLocalidad ? "#swalSelectLocalidad" : null,
					initial: loadSavedLocation ? _getSavedLocation() : initial,
				});
			},

			preConfirm: () => {
				if (!instance) return false;

				const values = instance.getValue();

				if (!values.paisId) {
					Swal.showValidationMessage("Seleccioná un país");
					return false;
				}

				if (!values.provinciaId) {
					Swal.showValidationMessage("Seleccioná una provincia");
					return false;
				}

				if (showLocalidad && !values.localidadId) {
					Swal.showValidationMessage("Seleccioná una localidad");
					return false;
				}

				if (loadSavedLocation) _saveLocation(values);

				return values;
			},

			willClose: () => {
				if (instance?.destroy) instance.destroy();
				instance = null;
			},
		});

		if (!result.isConfirmed) return null;
		return result.value;
	},

	/**
	 * Compatibilidad con tu ProvinciaService viejo:
	 * Devuelve solo provinciaId o null
	 * 
	 * Pide provincia pero:
	 * - si ya hay guardada, la devuelve sin molestar
	 * - si querés forzar picker: preguntarProvincia({ force: true })
	 * - si querés que muestre picker precargado igual: preguntarProvincia({ alwaysAsk: true })
		   */
	preguntarProvincia: async (paisId = null, opts = {}) => {

		const { force = false, alwaysAsk = false } = opts;

		const saved = _getSavedLocation();

		// si hay guardada y no forzás, devolvemos directo
		if (!force && !alwaysAsk && saved?.provinciaId) {
			return saved.provinciaId;
		}
		const result = await LocationSelectModule.pick({
			title: "¿En qué provincia estás?",
			showLocalidad: false,
			initial: {
				paisId: paisId ?? saved?.paisId ?? null,
				provinciaId: saved?.provinciaId ?? null,
				localidadId: null,
			},
		});

		if (!result?.provinciaId) return null;

		// guardamos para la próxima
		_saveLocation(result);

		return Number(result.provinciaId);
	},
};
